import { yupResolver } from '@hookform/resolvers/yup';
import { useTour } from '@reactour/tour';
import { useMutation } from '@tanstack/react-query';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { isChrome } from 'react-device-detect';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useWizard } from 'react-use-wizard';
import { useTheme } from 'styled-components';

import { Camera } from '@/components/common/camera';
import { ErrorAlert } from '@/components/common/error-alert';
import { UploadIcon } from '@/components/common/upload-icon';
import { Button, ButtonVariant } from '@/components/form/button';
import { Input } from '@/components/form/input';
import { Select } from '@/components/form/select';
import { SampleImages } from '@/features/demo/base64-sample-images/beer';
import { getTourStep } from '@/features/demo/steps';
import {
  IImage,
  useImageUpload,
} from '@/features/single-measurement/hooks/use-image-upload';
import { updateBatch } from '@/features/single-measurement/services/batches';
import { IBatch, State } from '@/features/single-measurement/types';
import { shouldRenderInput } from '@/features/single-measurement/utils/conditional-input-render';
import { getImageVolume } from '@/features/single-measurement/utils/get-image-volume';
import { getBatchCreationValidation } from '@/features/single-measurement/validations/batch-analysis-creation';
import { CameraType, getSugarUnitOptions, useSettings } from '@/features/user-settings';
import { fileToBase64 } from '@/utils/file-to-base64';
import { getImageDimensions } from '@/utils/get-image-dimensions';
import { isCameraCalibrated } from '@/utils/is-camera-calibrated';

import { ImageUploadPreview } from '../image-upload-preview';
import { IStep } from '../types';

export type ViabilityConcentrationFields = {
  name: string;
  density: number;
  imageVolume: number;
  turbidity: number;
  temperature: number;
  ph: number;
  description: string;
  dilutionStain1: number; // required
  dilutionStain2: number; // required
  dilutionSample1: number; // required
  dilutionSample2: number; // required
};

const dummyCameraDevices = [{ label: 'USB Camera', deviceId: '1_cam' }];

export const CreateBatchStep: React.FC<
  IStep & { onReset: () => void; onSuccess?: (data: IBatch) => void }
> = ({ state, onReset, onSuccess: _onSuccess }) => {
  const { t } = useTranslation();
  const theme: any = useTheme();

  const method = state.method!;
  const batchId = state.batchId!;

  // settings hook
  const { settings } = useSettings();
  const {
    imagePerAnalysis,
    sugarUnit,
    chamberHeight,
    defaultDilutionSample1,
    defaultDilutionSample2,
    defaultDilutionStain1,
    defaultDilutionStain2,
    ...restSettings
  } = settings;

  // using as the general error message
  const [generalError, setGeneralError] = useState('');
  const [imageLimitReachedError, setImageLimitReachedError] = useState('');

  // store the pixel height (diameter of the detection circle) of recently uploaded image to be used in volume calc
  const [camPixelHeight, setCamPixelHeight] = useState(0);

  // image upload hook
  const {
    images,
    count: currentImageCount,
    ...imageUpload
  } = useImageUpload(imagePerAnalysis);

  // ref for the local file upload input
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  /**
   * will be used to disable the camera button if the camera settings are invalid.
   * checkout 'validateCameraSettings' function for the implementation details.
   */
  const [disableCapture, setDisableCapture] = useState<boolean>(false);

  // based on the analysis method we switch the validation schema
  // only few input fields are getting removed from the UI based on the analysis method
  // if the validation schema become complex, consider creating two seperate validations for each type
  const {
    register,
    setValue,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = useForm<ViabilityConcentrationFields>({
    resolver: yupResolver(getBatchCreationValidation(method)),
  });

  // demo
  const { isOpen: isTourOpen, setCurrentStep, currentStep, steps } = useTour();

  useEffect(() => {
    if (isTourOpen) {
      setCurrentStep(getTourStep(steps, 'hxc1fUV'));
    }
  }, []);

  useEffect(() => {
    if (isTourOpen && currentStep === getTourStep(steps, 'hxc1fUV')) {
      // if on demo mode, we pre-populate 5 images
      for (let i = 0; i < SampleImages.length; i++) {
        const data = SampleImages[i];
        setTimeout(() => {
          imageUpload.add({ data, batchId, bypassValidation: true });
        }, i * 500);
      }
    }
  }, [currentStep]);

  const { previousStep } = useWizard();

  // update batch mutation
  const updateBatchMutation = useMutation({
    mutationFn: ({ id, batch }: { id: string; batch: Partial<IBatch> }) =>
      updateBatch(id, { ...batch }),
    onSuccess: (data: IBatch) => {
      _onSuccess && _onSuccess(data);
      toast.success(t('measurements.create.success'));
    },
  });

  // image will be sent to sharpness check and validation
  const onImageCapture = async (data: string | null) => {
    if (imageUpload.hasReachedMaxLimit.current) {
      setImageLimitReachedError('Image limit has reached');
      return;
    }

    // set height (diameter of detection circle) of image to be used in volume calc
    if (data) {
      const { height } = await getImageDimensions(data);
      setCamPixelHeight(height);
      imageUpload.add({ data, batchId, bypassValidation: false });
    }
  };

  // handled local/manual file uploads via the upload button
  const handleLocalFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const data = await fileToBase64(e.target.files[0]);
    onImageCapture(data);
  };

  // Below function validate the settings of camera, before capture images
  // Note: move out this function to a seperate util if needed to re-use
  const validateCameraSettings = ({
    width,
    height,
  }: {
    width: number;
    height: number;
    aspectRatio: number;
  }) => {
    // reset error when user switch between devices
    setGeneralError('');
    setDisableCapture(false);

    // check for calibration settings are correct
    const square = settings.detectedSquareSize;
    if (
      settings.camera === CameraType.Lab &&
      !isCameraCalibrated({ camera: settings.camera, detectedSquareSize: square })
    ) {
      setGeneralError('measurements.create.calibrationRequired');
    }

    // if selected camera is oculyze, camera must have resolution of 1920x1080
    // we check this only on chrome due to the firefox resolution limitation
    const isOculyzeCamResolution = width === 1920 && height === 1080;

    if (
      !isTourOpen &&
      isChrome &&
      settings.camera === CameraType.Oculyze &&
      !isOculyzeCamResolution
    ) {
      setGeneralError('Invalid camera settings, please calibrate');
      setDisableCapture(true);
    }
  };

  useEffect(() => {
    // auto fill in demo mode
    // we have delayed the input for better UX
    if (currentStep === getTourStep(steps, 'QhxTv0P'))
      setTimeout(function () {
        setValue('name', 'Demo measurement');
      }, 500);

    if (currentStep === getTourStep(steps, '1mddq44'))
      setTimeout(function () {
        setValue('description', 'This is my comment');
      }, 400);

    if (currentStep === getTourStep(steps, 'cBPkqlj'))
      setTimeout(function () {
        setValue('dilutionSample2', 99);
      }, 400);
  }, [currentStep]);

  const handleBack = () => {
    onReset();
    resetForm();
    setGeneralError('');
    imageUpload.clear();
    previousStep();
  };

  // is everything ok to procced
  const isEverythingOkToCreate = useMemo(
    () =>
      isTourOpen
        ? isTourOpen && currentImageCount === 5
        : imageUpload.hasReachedMaxLimit.current,
    [currentImageCount, imageUpload.hasReachedMaxLimit.current],
  );

  // update sugar unit label dynamically based on the user settings
  const sugarUnitLabel = useMemo(() => {
    return getSugarUnitOptions().find(({ value }) => value === sugarUnit)?.label;
  }, [sugarUnit]);

  // calculate image volume
  const imageVolume = getImageVolume({ chamberHeight, camPixelHeight, ...restSettings });

  const handleOnSubmit = handleSubmit((data) => {
    const batch: Partial<IBatch> = {
      ...data,
      imageVolume,
      scaleType: sugarUnit,
      state: State.UploadCompleted,
      methodAttributes: { stain: settings.stain, analysisLevel: settings.cellSize },
    };
    updateBatchMutation.mutate({ id: batchId, batch });
  });

  // remove image by clicking "X" in preview section
  const handlePreviewRemove = (image: IImage) => {
    if (image.taskId) {
      imageUpload.remove({ imageId: image.id, taskId: image.taskId, batchId });
      setImageLimitReachedError('');
    }
  };

  const renderCameraSettingsInfo = () => {
    if (disableCapture) return <></>;

    let text = '';

    if (settings.camera === CameraType.Oculyze) {
      text = t('measurements.cameraSettingsText.oculyze', { chamberHeight });
    }

    if (settings.camera === CameraType.Lab) {
      text = t('measurements.cameraSettingsText.lab');
    }

    return <p className="text-xs text-center text-gray-500">{text}</p>;
  };

  return (
    <>
      <div className="grid mt-4 md:grid-cols-2 md:mt-0 md:p-12 md:gap-14">
        {/* start: camera section */}
        <div>
          {isTourOpen ? (
            <DummyCameraPreview />
          ) : (
            <Camera
              limit={imagePerAnalysis}
              count={currentImageCount}
              onCapture={onImageCapture}
              disableCapture={disableCapture}
              onCameraMount={validateCameraSettings}
            />
          )}
          <div className="mt-1">{renderCameraSettingsInfo()}</div>
          <ImageUploadPreview images={images} onRemove={handlePreviewRemove} />
        </div>
        {/* end: camera section */}

        {/* start: form inputs */}
        <form>
          <div className="flex flex-col gap-5" id="step-information-on-sample">
            <div id="step-sample-dilution">
              <h2 className="mb-2 text-lg font-bold text-black-dark">
                {t('measurements.details.dilution')}
              </h2>

              {/* start: dilution inputs */}
              <div className="grid gap-2 p-2 md:grid-cols-2">
                <div className="flex items-start col-span-2 gap-2">
                  <div className="flex flex-col w-full gap-2">
                    <label htmlFor="dilutionSample1" className="text-gray-500">
                      {t('measurements.sample')}
                    </label>
                    <Input<ViabilityConcentrationFields>
                      min="0"
                      lang="de-DE"
                      type="number"
                      errors={errors}
                      defaultValue={defaultDilutionSample1}
                      register={register}
                      name="dilutionSample1"
                      labelStyleClass="text-lg text-gray-700"
                      className="w-full pl-6 font-bold text-center"
                    />
                  </div>
                  <div className="flex flex-col w-full gap-2">
                    <label htmlFor="dilutionSample2" className="text-gray-500">
                      {t('measurements.water')}
                    </label>
                    <Input<ViabilityConcentrationFields>
                      min="0"
                      lang="de-DE"
                      type="number"
                      errors={errors}
                      defaultValue={defaultDilutionSample2}
                      labelPos="bottom"
                      register={register}
                      name="dilutionSample2"
                      labelStyleClass="text-lg text-gray-700"
                      className="w-full pl-6 font-bold text-center"
                    />
                  </div>
                </div>
              </div>
              {/* start: dilution inputs */}
            </div>

            {shouldRenderInput({ input: 'dilutionStain1', method: method }) && (
              <div id="step-dilution-sample">
                <h2 className="mb-2 text-lg font-bold text-black-dark">
                  {t('editMeasurements.dilutionStaining')}
                </h2>

                {/* start: dilution inputs */}
                <div className="grid gap-2 p-2 md:grid-cols-2">
                  <div className="flex items-start col-span-2 gap-2">
                    {/* we only show below fields on conz and viab */}
                    <div className="flex flex-col w-full gap-2">
                      <label htmlFor="dilutionStain1" className="text-gray-500">
                        {t('measurements.dilutionSample')}
                      </label>
                      <Input<ViabilityConcentrationFields>
                        min="0"
                        lang="de-DE"
                        type="number"
                        errors={errors}
                        defaultValue={defaultDilutionStain1}
                        register={register}
                        name="dilutionStain1"
                        labelStyleClass="text-lg text-gray-700"
                        className="w-full pl-6 font-bold text-center"
                      />
                    </div>
                    <div className="flex flex-col w-full gap-2">
                      <label htmlFor="dilutionStain2" className="text-gray-500">
                        {t('measurements.colorSolution')}
                      </label>
                      <Input<ViabilityConcentrationFields>
                        min="0"
                        lang="de-DE"
                        type="number"
                        errors={errors}
                        defaultValue={defaultDilutionStain2}
                        register={register}
                        name="dilutionStain2"
                        labelStyleClass="text-lg text-gray-700"
                        className="w-full pl-6 font-bold text-center"
                      />
                    </div>
                  </div>
                </div>
                {/* start: dilution inputs */}
              </div>
            )}

            {/* start: general inputs */}
            <div className="flex flex-col gap-5 mt-1">
              <div id="step-batch-name">
                <Input<ViabilityConcentrationFields>
                  name="name"
                  errors={errors}
                  register={register}
                  labelStyleClass="w-1/2"
                  label={t('common.name')}
                  placeholder={t('common.name')}
                  className="w-full placeholder:font-bold"
                />
              </div>
              {shouldRenderInput({ input: 'density', method: method }) && (
                <div id="step-density">
                  <Input<ViabilityConcentrationFields>
                    lang="de-DE"
                    type="number"
                    name="density"
                    errors={errors}
                    register={register}
                    labelStyleClass="w-1/2"
                    placeholder={t('measurements.sugar')}
                    className="w-full placeholder:font-bold"
                    label={`${t('measurements.sugarContent')} (${t(
                      sugarUnitLabel as string,
                    )})`}
                  />
                </div>
              )}
              {shouldRenderInput({ input: 'turbidity', method: method }) && (
                <div id="step-turbidity">
                  <Input<ViabilityConcentrationFields>
                    lang="de-DE"
                    type="number"
                    errors={errors}
                    name="turbidity"
                    register={register}
                    labelStyleClass="w-1/2"
                    label={t('measurements.turbidity')}
                    className="w-full placeholder:font-bold"
                    placeholder={t('measurements.turbidity')}
                  />
                </div>
              )}
              {shouldRenderInput({ input: 'temperature', method: method }) && (
                <div id="step-temperature">
                  <Input<ViabilityConcentrationFields>
                    lang="de-DE"
                    type="number"
                    errors={errors}
                    name="temperature"
                    register={register}
                    labelStyleClass="w-1/2"
                    label={t('measurements.temperature_c')}
                    className="w-full placeholder:font-bold"
                    placeholder={t('measurements.temperature')}
                  />
                </div>
              )}
              {shouldRenderInput({ input: 'ph', method: method }) && (
                <div id="step-ph">
                  <Input<ViabilityConcentrationFields>
                    lang="de-DE"
                    type="number"
                    errors={errors}
                    name="ph"
                    register={register}
                    labelStyleClass="w-1/2"
                    label={t('measurements.details.ph')}
                    className="w-full placeholder:font-bold"
                    placeholder={t('measurements.details.ph')}
                  />
                </div>
              )}
              <div id="step-batch-description">
                <Input<ViabilityConcentrationFields>
                  errors={errors}
                  register={register}
                  name="description"
                  labelStyleClass="w-1/2"
                  label={t('common.comment')}
                  placeholder={t('common.comment')}
                  className="w-full placeholder:font-bold"
                />
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLocalFileUpload}
                style={{ display: 'none' }}
              />
              <ErrorAlert message={generalError} />
              <ErrorAlert message={imageLimitReachedError} />
            </div>
            {/* end: general inputs */}
          </div>
        </form>
        {/* end: form inputs */}
      </div>

      {/* start: footer */}
      <div className="flex flex-col justify-between gap-4 mt-4 md:flex-row md:mt-0 md:gap-0">
        <div className="md:pl-12">
          <Button
            variant={ButtonVariant.Link}
            onClick={() => fileInputRef.current?.click()}
            disabled={updateBatchMutation.isLoading}
            classnames={`text-${theme.current}-primary font-bold flex items-start gap-2`}
          >
            <UploadIcon />
            {t('measurements.create.choose')}
          </Button>
        </div>

        <div className="flex justify-between gap-5">
          <Button
            onClick={handleBack}
            variant={ButtonVariant.Secondary}
            disabled={updateBatchMutation.isLoading}
          >
            {t('common.back')}
          </Button>
          <Button
            id="step-batch-create-btn"
            type="submit"
            onClick={handleOnSubmit}
            disabled={!isEverythingOkToCreate}
            isLoading={updateBatchMutation.isLoading}
          >
            {t('common.analyze')}
          </Button>
        </div>
      </div>
    </>
  );
};

const DummyCameraPreview = () => {
  return (
    <div className="flex flex-col w-full gap-2">
      <Select
        id="step-camera-selection"
        options={[
          ...dummyCameraDevices.map(({ label, deviceId }) => ({
            label,
            value: deviceId,
          })),
        ]}
        menuItemClassnames="w-full"
        defaultValue={'1_cam'}
      />
      <div
        id="step-camera-selection"
        className="flex items-center justify-center text-gray-500 bg-gray-100 rounded-md h-72"
      >
        <div className="flex flex-col items-center">
          <CameraIcon />
          <p>Camera Image Preview</p>
        </div>
      </div>
    </div>
  );
};

const CameraIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-6 h-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z"
      />
    </svg>
  );
};
